import { IDiagnosis, IPatient, NEW_DIAGNOSIS_ID } from '../caremap/ICareMap';
import { SupportGroupDataProvider } from '../caremap/SupportGroupDataProvider';
import { LabelProvider } from '../config/LabelProvider';
import { DuoClipboardListIcon } from '../theme/Icons';
import { DiagnosisSelectField } from './DiagnosisSelectField';
import { IStructuredFieldDef, IStructuredRecordDescriptor, IStructuredRecordIsPublicKey, IStructuredRecordPatientKey } from './structured-records/StructuredRecordDescriptor';

export class DiagnosisDescriptor implements IStructuredRecordDescriptor<IDiagnosis> {
    static inject = () => [SupportGroupDataProvider, LabelProvider];

    constructor(
        private dataProvider: SupportGroupDataProvider,
        private labelProvider: LabelProvider
    ) {}

    patientKey: IStructuredRecordPatientKey = 'diagnoses';
    idField: keyof IDiagnosis = 'id';
    newId = NEW_DIAGNOSIS_ID;
    mode = 'edit-diagnosis';
    routeSegment = 'diagnoses';
    singular = 'Diagnosis';
    plural = 'Diagnoses';
    Icon = DuoClipboardListIcon;
    isPublicKey: IStructuredRecordIsPublicKey = 'diagnosesIsPublic';

    trackingNoun = 'Diagnosis';

    getName = (diagnosis: IDiagnosis) => diagnosis.diagnosis;

    buildTrackingProps = (diagnosis: IDiagnosis) => ({
        'Status': diagnosis.status,
        'Has Date': Boolean(diagnosis.diagnosedDate),
        'Has Notes': Boolean(diagnosis.notes)
    });

    getRecords = (patient: IPatient) => patient.diagnoses;
    withRecords = (patient: IPatient, records: IDiagnosis[]): IPatient => ({ ...patient, diagnoses: records });
    createBlank = (id: string): IDiagnosis => ({ id });

    // a getter (not a stored field) so the diagnosis-name label re-reads LabelProvider's
    // use_MY_CAREMAP__PrefixIfApplicable flag on every access. That flag flips per active caremap
    // (see CareMapState, which is the only writer), but this descriptor is a long-lived IoC
    // singleton -- a one-time-computed label would freeze at whichever caremap was active when
    // the container first constructed this instance, and never update again as the user switches
    // care recipients.
    get fields(): IStructuredFieldDef<IDiagnosis>[] {
        const label = this.labelProvider.getLabel('CARE_RECIPIENT_DIAGNOSIS');

        return [
            {
                key: 'diagnosis',
                valueKey: 'diagnosisId',
                label,
                type: 'searchable-select',
                required: true,
                getSelectOptions: () => this.dataProvider.getDiagnosisSelectOptions().map(option => ({ id: option.value, label: option.label })),
                // reuse DiagnosisSelectField verbatim -- it writes the diagnosisId/diagnosis pair AND
                // fires the 'Custom Type Added' metric for free-typed values, neither of which the
                // generic searchable-select atom above knows how to do.
                render: ({ record, readOnly, patch }) => (
                    <DiagnosisSelectField
                        label={label}
                        diagnosisId={record.diagnosisId}
                        diagnosis={record.diagnosis}
                        onChange={d => patch(d)}
                        readOnly={readOnly}
                    />
                )
            },
            {
                key: 'status',
                label: 'Status',
                type: 'enum-select',
                enumOptions: [
                    { value: 'active', label: 'Active' },
                    { value: 'resolved', label: 'Resolved' }
                ]
            },
            {
                key: 'diagnosedDate',
                label: 'Date diagnosed',
                type: 'date'
            },
            {
                key: 'notes',
                label: 'Notes',
                type: 'textarea'
            }
        ];
    }
}
